import { format } from '../i18n/format'
import { t } from '../i18n/pt-BR'

/**
 * O painel do desafio (modo_historia/05#HM-001). docs/21 secao 8. DOM sobre o canvas, com textos do
 * dicionario; um estado por vez:
 *
 * - **alvo**: o jogador tocado, com "Desafiar" se ele esta ao alcance, ou o pedido para chegar mais perto;
 * - **recebido**: quem desafiou, "Aceitar", "Recusar" e a contagem do prazo;
 * - **enviado**: "esperando responder", com a contagem;
 * - **aviso**: por que o desafio fechou, ou que o duelo esta sendo preparado.
 */

type Clock = { now: () => number; every: (ms: number, run: () => void) => () => void }

const realClock: Clock = {
  now: () => Date.now(),
  every: (ms, run) => {
    const id = setInterval(run, ms)
    return () => clearInterval(id)
  },
}

export type ChallengePanel = {
  element: HTMLElement
  target(nick: string, inRange: boolean, onChallenge: () => void): void
  incoming(nick: string, expiresAt: number, onAnswer: (accept: boolean) => void): void
  pending(nick: string, expiresAt: number): void
  notice(text: string): void
  clear(): void
}

export function challengePanel(clock: Clock = realClock): ChallengePanel {
  const element = document.createElement('section')
  element.className = 'challenge'
  element.setAttribute('aria-live', 'polite')
  element.hidden = true
  let stopCountdown: (() => void) | null = null

  const reset = (): void => {
    stopCountdown?.()
    stopCountdown = null
    element.replaceChildren()
    element.hidden = false
  }
  const line = (text: string): HTMLParagraphElement => {
    const p = document.createElement('p')
    p.textContent = text
    return p
  }
  const button = (text: string, onClick: () => void, secondary = false): HTMLButtonElement => {
    const b = document.createElement('button')
    b.type = 'button'
    b.className = secondary ? 'button button-secondary' : 'button'
    b.textContent = text
    b.addEventListener('click', onClick)
    return b
  }
  const countdown = (expiresAt: number): HTMLSpanElement => {
    const span = document.createElement('span')
    span.className = 'countdown'
    const draw = (): void => {
      span.textContent = format(t.challenge.seconds, { n: Math.max(0, Math.ceil((expiresAt - clock.now()) / 1000)) })
    }
    draw()
    stopCountdown = clock.every(250, draw)
    return span
  }

  const panel: ChallengePanel = {
    element,
    target(nick, inRange, onChallenge) {
      reset()
      if (inRange) element.append(button(format(t.challenge.challenge, { nick }), onChallenge))
      else element.append(line(format(t.challenge.tooFar, { nick })))
      element.append(button(t.challenge.close, () => panel.clear(), true))
    },
    incoming(nick, expiresAt, onAnswer) {
      reset()
      element.setAttribute('role', 'alertdialog')
      element.append(
        line(format(t.challenge.incoming, { nick })),
        countdown(expiresAt),
        button(t.challenge.accept, () => onAnswer(true)),
        button(t.challenge.decline, () => onAnswer(false), true),
      )
    },
    pending(nick, expiresAt) {
      reset()
      element.append(line(format(t.challenge.pending, { nick })), countdown(expiresAt))
    },
    notice(text) {
      reset()
      element.append(
        line(text),
        button(t.challenge.close, () => panel.clear(), true),
      )
    },
    clear() {
      stopCountdown?.()
      stopCountdown = null
      element.replaceChildren()
      element.removeAttribute('role')
      element.hidden = true
    },
  }
  return panel
}

/** O texto de um desafio que fechou, a partir do motivo da sala e de quem e a recusa. */
export function closedText(reason: string, about: string | undefined, self: string): string {
  if (reason === 'INVALID_DECK' && about === self) return t.challenge.yourDeck
  const known = t.challenge.closed as Record<string, string>
  return known[reason] ?? t.challenge.closed.UNAVAILABLE
}
